import {
  inboundCallError,
  type InboundCall,
  type InboundCallError,
  type InboundCallUser,
  type InboundIvrStep,
} from "@/api/client";
import { fmtPhone } from "@/lib/phoneFormat";
import type { CallStatus } from "@/pages/Chats/CallModal";

// Список всплывашек входящих звонков — чистый редьюсер, без React и таймеров:
// время приходит в действиях (`now`), поэтому всё поведение проверяется тестом.
//
// Источников два, и они гоняются друг с другом:
//   • снимок `GET /chat/call/inbound` — при монтировании и на каждом
//     (пере)подключении потока: события, пропущенные за разрыв, не вернутся;
//   • события SSE `inbound_call` — на каждое изменение звонка, строго по
//     порядку (их публикует один процесс chat-service).
// Снимок может прийти ПОСЛЕ более свежего события (запрос ушёл раньше, чем
// звонок закончился или его взяли). Поэтому:
//   • у каждого известия есть время, на которое оно верно (`infoAt`: событие —
//     когда пришло, снимок — когда его запросили); известие старше того, что
//     мы уже знаем о звонке, не применяется;
//   • навсегда (`closed`) закрывает только настоящий конец — событие `ended` —
//     и «Скрыть» рекрутёра;
//   • «взят» — не конец: перехват может не состояться (Bridge на АТС не
//     сработал), и бекенд вернёт звонок в `ringing` — всплывашка должна
//     вернуться вместе с ним. Даже если окно звонка уже показало «Звонок
//     принял(а) …» и закрылось;
//   • отсутствие в снимке — тоже не конец, а только повод убрать всплывашку:
//     следующее событие о звонке вернёт её. И вывод этот делается лишь из
//     снимка после полной сверки бекенда с АТС (`resynced`): только что
//     перезапущенный chat-service отдаёт пустой список, пока не сверился.

/** Сколько висит «Взял(а) звонок: Имя», прежде чем уехать. */
export const TAKEN_LINGER_MS = 4000;
/** Длительность анимации ухода (inboundCall.css, icall-out). */
export const EXIT_MS = 220;
/** Сколько помнить закрытые и взятые звонки — дольше любого запоздалого снимка. */
export const CLOSED_TTL_MS = 30 * 60 * 1000;

// live    — звонящий в меню, всплывашка с кнопкой «Открыть звонок»;
// taken   — взял другой рекрутёр: показываем кто, потом уезжает;
// leaving — играет анимация ухода, после EXIT_MS пропадёт из списка.
export type ToastPhase = "live" | "taken" | "leaving";

export interface ToastItem {
  call: InboundCall;
  phase: ToastPhase;
  // Когда перейти к следующей фазе; null — пока звонок идёт.
  expiresAt: number | null;
  // Когда эта вкладка узнала о звонке.
  seenAt: number;
  // На какой момент верно то, что мы знаем о звонке (`call`, фаза): новее —
  // события, пришедшие позже, и снимки, запрошенные позже.
  infoAt: number;
}

export interface InboundCallsState {
  items: ToastItem[];
  // id → когда закрыт навсегда (событие `ended`, скрыт рекрутёром).
  closed: Record<string, number>;
  // id → на какой момент известно, что звонок взят (или что его итог уже
  // показало окно звонка). Не приговор: откат в `ringing` его снимает. Нужен,
  // когда всплывашки уже нет, — чтобы запоздалый снимок не вернул взятый
  // звонок «в меню».
  taken: Record<string, number>;
}

export const initialInboundCalls: InboundCallsState = { items: [], closed: {}, taken: {} };

export type InboundCallsAction =
  // requestedAt — когда снимок запросили (не когда он пришёл). resynced —
  // бекенд уже сверился с АТС: только тогда отсутствие звонка что-то значит.
  | {
      type: "snapshot";
      calls: InboundCall[];
      resynced: boolean;
      me: string | null;
      now: number;
      requestedAt: number;
    }
  | { type: "event"; call: InboundCall; me: string | null; now: number }
  // Рекрутёр скрыл всплывашку у себя; на сам звонок это не влияет.
  | { type: "dismiss"; id: string; now: number }
  // Убрать сразу, без анимации ухода: итог звонка уже показало окно звонка,
  // и всплывашка, выехавшая после его закрытия, была бы повтором. Не
  // хоронит: откат перехвата (событие `ringing`) вернёт всплывашку.
  | { type: "drop"; id: string; now: number }
  | { type: "tick"; now: number };

function startedMs(call: InboundCall): number {
  const t = Date.parse(call.started_at);
  return Number.isFinite(t) ? t : 0;
}

function byStart(a: ToastItem, b: ToastItem): number {
  // Старший звонок сверху: он ждёт дольше всех, а новые въезжают снизу и не
  // двигают те, в которые рекрутёр уже целится мышью.
  return startedMs(a.call) - startedMs(b.call) || a.call.id.localeCompare(b.call.id);
}

function leave(item: ToastItem, now: number, call: InboundCall = item.call): ToastItem {
  return { ...item, call, phase: "leaving", expiresAt: now + EXIT_MS };
}

/** Применить одно известие о звонке (из события или снимка). Мутирует копии.
 * `at` — на какой момент известие верно: для события это `now`, для снимка —
 * когда его запросили. */
function applyCall(
  items: ToastItem[],
  closed: Record<string, number>,
  taken: Record<string, number>,
  call: InboundCall,
  me: string | null,
  now: number,
  at: number,
): void {
  if (closed[call.id] !== undefined) {
    // Уже закрыт навсегда: ни обновлять, ни возвращать нечего.
    return;
  }
  const idx = items.findIndex((i) => i.call.id === call.id);
  const cur = idx >= 0 ? items[idx] : null;
  // Мы уже знаем о звонке что-то новее этого известия (запоздалый снимок).
  const knownAt = Math.max(cur?.infoAt ?? -Infinity, taken[call.id] ?? -Infinity);
  if (at < knownAt) return;

  if (call.state === "ended") {
    closed[call.id] = now;
    delete taken[call.id];
    if (cur && cur.phase !== "leaving") items[idx] = leave(cur, now, call);
    return;
  }

  if (call.state === "taken") {
    taken[call.id] = at;
    // Звонок, о котором мы не показывали, взяли — объявлять нечего. Сюда же
    // звонок, взятый ещё до перезапуска chat-service (taken_by: null), —
    // «Открыть звонок» у него быть не может.
    if (!cur) return;
    if (cur.phase === "leaving") {
      items[idx] = { ...cur, call, infoAt: at };
      return;
    }
    const mine = me !== null && call.taken_by?.user_id === me;
    // Взяли мы сами: дальше им занимается окно звонка.
    if (mine) items[idx] = { ...leave(cur, now, call), infoAt: at };
    else items[idx] = { ...cur, call, phase: "taken", expiresAt: now + TAKEN_LINGER_MS, infoAt: at };
    return;
  }

  // ringing. Звонок снова (или всё ещё) в меню: взятие откатилось — перехват
  // не удался, — или всплывашка уезжала только потому, что звонка не было в
  // снимке. В обоих случаях она возвращается.
  delete taken[call.id];
  if (cur) {
    if (cur.phase === "live") items[idx] = { ...cur, call, infoAt: at };
    else {
      items[idx] = {
        call,
        phase: "live",
        expiresAt: null,
        // Уезжавшая всплывашка въезжает заново — как новая.
        seenAt: cur.phase === "leaving" ? now : cur.seenAt,
        infoAt: at,
      };
    }
    return;
  }
  items.push({ call, phase: "live", expiresAt: null, seenAt: now, infoAt: at });
}

export function reduceInboundCalls(
  state: InboundCallsState,
  action: InboundCallsAction,
): InboundCallsState {
  switch (action.type) {
    case "event": {
      const items = [...state.items];
      const closed = { ...state.closed };
      const taken = { ...state.taken };
      applyCall(items, closed, taken, action.call, action.me, action.now, action.now);
      items.sort(byStart);
      return { items, closed, taken };
    }
    case "snapshot": {
      const items = [...state.items];
      const closed = { ...state.closed };
      const taken = { ...state.taken };
      const present = new Set<string>();
      for (const call of action.calls) {
        present.add(call.id);
        applyCall(items, closed, taken, call, action.me, action.now, action.requestedAt);
      }
      // Снимок после сверки с АТС отдаёт все незакончившиеся звонки. Нет в нём
      // живого — скорее всего, закончился, пока поток лежал; но только если
      // всё, что мы о нём знаем, старше запроса. Всплывашку убираем, но не
      // хороним: следующее событие о звонке вернёт её.
      if (action.resynced) {
        for (let i = 0; i < items.length; i++) {
          const it = items[i];
          if (it.phase === "live" && !present.has(it.call.id) && it.infoAt < action.requestedAt) {
            items[i] = { ...leave(it, action.now), infoAt: action.requestedAt };
          }
        }
      }
      items.sort(byStart);
      return { items, closed, taken };
    }
    case "dismiss": {
      const idx = state.items.findIndex((i) => i.call.id === action.id);
      if (idx < 0) return state;
      const items = [...state.items];
      if (items[idx].phase !== "leaving") items[idx] = leave(items[idx], action.now);
      return { ...state, items, closed: { ...state.closed, [action.id]: action.now } };
    }
    case "drop": {
      const items = state.items.filter((i) => i.call.id !== action.id);
      // Настоящий конец редьюсер уже похоронил сам (событие `ended`). Иначе
      // окно показало «взял такой-то» (или вывело конец из отсутствия в
      // снимке) — это ещё может откатиться. Запоминаем лишь, на какой момент
      // итог известен: снимок, запрошенный раньше, звонок «в меню» не вернёт,
      // а событие `ringing` — вернёт.
      if (state.closed[action.id] !== undefined) return { ...state, items };
      return { ...state, items, taken: { ...state.taken, [action.id]: action.now } };
    }
    case "tick": {
      let changed = false;
      const items: ToastItem[] = [];
      for (const it of state.items) {
        if (it.expiresAt === null || it.expiresAt > action.now) {
          items.push(it);
          continue;
        }
        changed = true;
        if (it.phase === "taken") items.push(leave(it, action.now));
        // leaving (или live с истёкшим сроком, чего не бывает) — убираем.
      }
      const prune = (src: Record<string, number>) => {
        const out: Record<string, number> = {};
        for (const [id, at] of Object.entries(src)) {
          if (action.now - at < CLOSED_TTL_MS) out[id] = at;
          else changed = true;
        }
        return out;
      };
      const closed = prune(state.closed);
      const taken = prune(state.taken);
      return changed ? { items, closed, taken } : state;
    }
  }
}

/** Можно ли из всплывашки открыть звонок: он живой и в меню. Взятый (в том
 * числе принятый до перезапуска chat-service, без имени) — нельзя. */
export function toastCanOpen(item: Pick<ToastItem, "phase" | "call">): boolean {
  return item.phase === "live" && item.call.state === "ringing";
}

/** Почему «Открыть звонок» недоступна: коротко — строкой под кнопкой,
 * подробно — во всплывающей подсказке. */
export interface OpenBlock {
  hint: string;
  title: string;
}

/** null — открыть можно. `busy` — во вкладке уже есть звонок (окно входящего
 * или исходящий из карточки): второй сеанс SIP-пользователя 101 сорвал бы
 * первый. */
export function openBlockedReason(call: Pick<InboundCall, "intercept_ready">, busy: boolean): OpenBlock | null {
  // intercept_ready = на АТС стоит план набора HRDeva И AMI-сессия с ней
  // жива: без связи слушать и перехватывать нечем, звонок только видно.
  // Меняется в обе стороны — бекенд переиздаёт все живые звонки.
  if (!call.intercept_ready) {
    return {
      hint: "АТС сейчас не готова к перехвату",
      title:
        "Нет связи с АТС или на ней не установлен план набора HRDeva — слушать и перехватывать звонки сейчас нельзя.",
    };
  }
  if (busy) return { hint: "Вы уже на звонке", title: "Вы уже на звонке — сначала завершите его." };
  return null;
}

/** Свежая версия звонка поверх известной (событие SSE — они идут по порядку).
 * Назад нельзя только из `ended`: это конец навсегда. А `taken → ringing`
 * бывает на самом деле — перехват не состоялся, звонящий снова в меню. */
export function mergeCall(prev: InboundCall, next: InboundCall): InboundCall {
  return prev.state === "ended" && next.state !== "ended" ? prev : next;
}

// ---- звонок в окне: те же два источника ----

export interface SessionCall {
  call: InboundCall;
  // На какой момент верно `call` (как infoAt у всплывашки).
  callAt: number;
}

/** Событие SSE о звонке, открытом в окне. */
export function sessionOnEvent<S extends SessionCall>(s: S, call: InboundCall, now: number): S {
  if (s.call.id !== call.id) return s;
  const merged = mergeCall(s.call, call);
  return merged === s.call ? s : { ...s, call: merged, callAt: now };
}

/** Снимок для звонка, открытого в окне. */
export function sessionOnSnapshot<S extends SessionCall>(
  s: S,
  snap: { calls: InboundCall[]; resynced: boolean; requestedAt: number },
): S {
  // Запрошен раньше, чем мы узнали то, что знаем, — ничего нового в нём нет.
  if (snap.requestedAt <= s.callAt) return s;
  const fresh = snap.calls.find((c) => c.id === s.call.id);
  if (fresh) {
    const merged = mergeCall(s.call, fresh);
    return merged === s.call ? s : { ...s, call: merged, callAt: snap.requestedAt };
  }
  // Нет в снимке. «Кончился, пока поток лежал» выводим только для звонка,
  // который у нас ещё в меню, и только из снимка после сверки с АТС. Взятый
  // звонок после перезапуска chat-service в список может и не попасть, а
  // судьбу разговора решает линия (BYE от АТС), не бекенд.
  if (!snap.resynced || s.call.state !== "ringing") return s;
  return { ...s, call: { ...s.call, state: "ended" }, callAt: snap.requestedAt };
}

/** Ближайший момент, когда списку нужен tick; null — не нужен. */
export function nextExpiry(state: InboundCallsState): number | null {
  let next: number | null = null;
  for (const it of state.items) {
    if (it.expiresAt !== null && (next === null || it.expiresAt < next)) next = it.expiresAt;
  }
  return next;
}

// ---- подписи ----

const IVR_LABEL: Record<InboundIvrStep, string> = {
  client: "клиент",
  candidate: "кандидат",
  partner: "партнёр",
  other: "другое",
};

/** Что звонящий выбрал в меню: 1 клиент, 2 кандидат, 3 партнёр, 0 другое. */
export function ivrLabel(step: InboundIvrStep | null | undefined): string | null {
  return step ? (IVR_LABEL[step] ?? null) : null;
}

/** Номер для показа: канонический — красиво, иначе как прислал шлюз, если там
 * вообще есть цифры («anonymous», пусто — это скрытый номер). */
export function callerNumber(call: Pick<InboundCall, "number" | "number_raw">): string | null {
  if (call.number) return fmtPhone(call.number);
  const raw = (call.number_raw ?? "").trim();
  return /\d/.test(raw) ? raw : null;
}

/** Заголовок: имя из базы, иначе номер, иначе «Скрытый номер». */
export function callerTitle(call: Pick<InboundCall, "number" | "number_raw" | "person">): string {
  return call.person?.name?.trim() || callerNumber(call) || "Скрытый номер";
}

/** «Слушает: Анна» / «Слушают: Анна, Олег» / «… и ещё 2». null — никто. */
export function listenersLabel(listeners: InboundCallUser[] | null | undefined): string | null {
  const names = (listeners ?? []).map((l) => l.name?.trim() || "коллега");
  if (!names.length) return null;
  if (names.length === 1) return `Слушает: ${names[0]}`;
  const head = names.slice(0, 2).join(", ");
  return names.length > 2 ? `Слушают: ${head} и ещё ${names.length - 2}` : `Слушают: ${head}`;
}

/** Сколько секунд идёт звонок. Часы сервера и браузера расходятся — не меньше 0. */
export function elapsedSec(startedAt: string, now: number): number {
  const t = Date.parse(startedAt);
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 1000));
}

/** 0:07, 2:45, 1:02:03 — таймер всплывашки. */
export function fmtClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

// ---- окно звонка ----
//
// Что показывает окно, складывается из трёх источников, и они приходят в любом
// порядке: линия софтфона (BYE от АТС может опередить событие «взял
// такой-то»), состояние звонка на сервере (SSE) и ответы /listen и /take.

export type ModalPhase =
  | "connecting" // запрашиваем прослушку и поднимаем линию
  | "listening" // слышим звонящего и автоответчик, нас не слышно
  | "taking" // POST /take в пути
  | "in_call" // разговор
  | "ended" // звонок закончился
  | "dropped" // оборвалась только наша линия, звонящий ещё в меню
  | "taken_other" // взял кто-то другой (или мы сами — в другой вкладке)
  | "error";

export interface ModalInput {
  call: Pick<InboundCall, "state" | "taken_by">;
  line: CallStatus["state"];
  listenError: InboundCallError | null;
  me: string | null;
  // Исход нашего /take неизвестен: запрос в пути или ответ на него так и не
  // пришёл (сеть). Линию при этом могли уже перекинуть на звонящего.
  takeUnsure: boolean;
  took: boolean;
  taking: boolean;
  // Ответ /take: 409 taken — с чужим taken_by или с нашим (другая вкладка).
  takenOther: InboundCallUser | null;
  // Ответ /take: 404/410.
  gone: boolean;
  // Ответ /take: 409 listen_lost — бекенд нашу прослушку не знает (например,
  // перезапустился). Эту линию перехватить уже нельзя.
  listenLost: boolean;
}

const SOMEONE: InboundCallUser = { user_id: "", name: "" };

// Линия ещё жива (или поднимается): её можно положить.
const LIVE_LINE: ReadonlySet<CallStatus["state"]> = new Set(["connecting", "ringing", "listening", "in_call"]);

function isTakenError(err: InboundCallError | null): boolean {
  if (!err || err.status !== 409) return false;
  return err.code === "taken" || (err.code === null && err.taken_by !== null);
}

/** Кто взял звонок, если не мы в этом окне; null — никто (или мы). */
function takerOf(i: ModalInput): InboundCallUser | null {
  if (i.took) return null;
  if (i.takenOther) return i.takenOther;
  if (isTakenError(i.listenError)) return i.listenError?.taken_by ?? SOMEONE;
  if (i.call.state !== "taken") return null;
  const by = i.call.taken_by;
  const byMe = !!by && i.me !== null && by.user_id === i.me;
  // Исход нашего /take неизвестен (запрос в пути или ответа так и не было), а
  // взяли вы — или неизвестно кто: звонок, взятый до перезапуска
  // chat-service, приходит без имени. Возможно, это нашу линию АТС уже
  // перекинула на звонящего, и класть её нельзя — вместе с ней АТС положит и
  // звонящего (Bridge с флагом x). Успехом это тоже не считаем: решает ответ
  // /take (повтор тем же токеном вернёт 200) или сама линия. Чужое имя —
  // уже ответ; кончившаяся линия — тоже: беречь больше нечего.
  if ((i.takeUnsure || i.taking) && (byMe || !by) && LIVE_LINE.has(i.line)) return null;
  return by ?? SOMEONE;
}

export function modalPhase(i: ModalInput): { phase: ModalPhase; taker: InboundCallUser | null } {
  // Разговор уже наш: дальше решает только линия. Ни «ended», ни пропажа из
  // снимка, ни чужой taken_by с бекенда его не кладут — после перезапуска
  // chat-service не знает о взятом звонке, а BYE на нашей линии АТС через
  // Bridge(…, x) превратила бы в сброс звонящего посреди фразы. Разговор
  // кончается, когда кончается линия (JsSIP `ended`).
  if (i.took && i.line === "in_call") return { phase: "in_call", taker: null };
  const taker = takerOf(i);
  if (taker) return { phase: "taken_other", taker };
  const le = i.listenError;
  if (i.gone || i.call.state === "ended" || (le && (le.status === 404 || le.status === 410))) {
    return { phase: "ended", taker: null };
  }
  if (le) return { phase: "error", taker: null };
  if (i.listenLost) return { phase: "dropped", taker: null };
  switch (i.line) {
    case "ended":
      // Линия кончилась после перехвата, а звонок снова в меню: Bridge на
      // АТС не состоялся, бекенд вернул звонок в ringing — можно слушать снова.
      if (i.took) return { phase: i.call.state === "ringing" ? "dropped" : "ended", taker: null };
      return { phase: "dropped", taker: null };
    case "error":
      return { phase: "error", taker: null };
    case "in_call":
      return { phase: "in_call", taker: null };
    case "listening":
      return { phase: i.taking ? "taking" : "listening", taker: null };
    default:
      return { phase: "connecting", taker: null };
  }
}

/** Класть ли свою линию, не дожидаясь BYE: у звонка есть итог (кончился, его
 * взяли), а линия ещё жива. Разговора это не касается — в нём фаза держится
 * на линии (см. modalPhase). */
export function hangsUpLine(phase: ModalPhase, line: CallStatus["state"]): boolean {
  return (phase === "ended" || phase === "taken_other") && LIVE_LINE.has(line);
}

/** Строка состояния для «dropped»: почему оборвалась наша линия. */
export function droppedText(i: { took: boolean; listenLost: boolean }): string {
  if (i.listenLost) return "Прослушка прервалась — откройте звонок заново";
  if (i.took) return "Соединить со звонящим не удалось — он всё ещё в меню";
  return "Прослушка прервалась — звонящий всё ещё в меню";
}

/** Подсказка в окне, когда номера нет: запись перехваченного разговора
 * ложится в чат по номеру звонящего, а без него ей некуда. */
export function missingNumberHint(call: Pick<InboundCall, "number" | "number_raw">): string | null {
  if (call.number) return null;
  return callerNumber(call)
    ? "Номер не распознан — запись разговора в чат не попадёт"
    : "Скрытый номер — запись разговора в чат не попадёт";
}

/** «Звонок принял(а) Анна» / «Вы приняли звонок в другой вкладке». */
export function takenText(taker: InboundCallUser, me: string | null): string {
  if (me !== null && taker.user_id === me) return "Вы приняли этот звонок в другой вкладке";
  const name = taker.name?.trim();
  return name ? `Звонок принял(а) ${name}` : "Звонок принял другой рекрутёр";
}

/** Почему не поднялась прослушка (ответ /listen). 409/404/410 сюда не
 * попадают: это не ошибка, а итог звонка. */
export function listenErrorText(err: InboundCallError): string {
  if (err.code === "pbx_dialplan_missing") {
    return "На АТС не установлен план набора HRDeva — слушать и перехватывать звонки нельзя.";
  }
  if (err.code === "inbound_disabled") return "Входящие звонки на этом стенде выключены.";
  if (err.status === 503) return "Нет связи с АТС — попробуйте ещё раз.";
  if (err.status === 0) return "Нет связи с сервером — попробуйте ещё раз.";
  return `Не удалось подключиться к звонку (код ${err.status}).`;
}

// ---- перехват ----

/** Итог POST /take (после возможного повтора). */
export type TakeResult = { ok: true } | { ok: false; err: InboundCallError | null };

/** Ответа от бекенда нет: сеть, обрыв, прокси без chat-service за ним (502/504
 * без нашего `code`). Перехват при этом мог и состояться. */
export function takeNoAnswer(err: InboundCallError | null): boolean {
  if (!err || err.status === 0) return true;
  return err.code === null && (err.status === 502 || err.status === 504);
}

const RETRY_PAUSE_MS = 700;

/** POST /take с одним повтором, если ответа не было. Повтор безопасен: тот же
 * токен бекенд второй раз признаёт своим и отвечает 200 (перехват
 * идемпотентен по токену), а чужой победитель — это 409 при любом раскладе. */
export async function takeWithRetry(
  send: () => Promise<unknown>,
  pause: () => Promise<void> = () => new Promise((r) => setTimeout(r, RETRY_PAUSE_MS)),
): Promise<TakeResult> {
  const once = async (): Promise<TakeResult> => {
    try {
      await send();
      return { ok: true };
    } catch (e) {
      return { ok: false, err: inboundCallError(e) };
    }
  };
  const first = await once();
  if (first.ok || !takeNoAnswer(first.err)) return first;
  await pause();
  return once();
}

export type TakeOutcome =
  | { kind: "taken"; by: InboundCallUser }
  | { kind: "gone" }
  | { kind: "lost" }
  // unsure — ответа так и не было: линию могли и перекинуть.
  | { kind: "error"; message: string; unsure: boolean };

/** Разбор отказа /take. 409 taken — всегда «взял другой», даже если taken_by
 * — это мы: своей вкладке бекенд на повтор тем же токеном отвечает 200, так
 * что 409 с нашим именем — это победила другая наша вкладка. */
export function takeOutcome(err: InboundCallError | null): TakeOutcome {
  if (isTakenError(err)) return { kind: "taken", by: err?.taken_by ?? SOMEONE };
  if (err && err.status === 409 && err.code === "listen_lost") return { kind: "lost" };
  if (err && (err.status === 404 || err.status === 410)) return { kind: "gone" };
  return { kind: "error", message: takeErrorText(err), unsure: takeNoAnswer(err) };
}

function takeErrorText(err: InboundCallError | null): string {
  // Ответа нет и после повтора: сеть лежит. Если перехват всё-таки прошёл,
  // следующий клик тем же токеном получит 200.
  if (takeNoAnswer(err) || !err) return "Нет ответа от сервера — попробуйте ещё раз.";
  if (err.code === "not_listening") {
    return "АТС ещё не подключила прослушку — попробуйте через пару секунд.";
  }
  // Токен выдан другому пользователю (403 not_your_listener) — например, в
  // этой вкладке сменили вход. Забытый бекендом токен — это 409 listen_lost.
  if (err.status === 403) return "Линия прослушки уже закрыта — подключитесь к звонку заново.";
  if (err.code === "pbx_dialplan_missing") {
    return "На АТС не установлен план набора HRDeva — перехват недоступен.";
  }
  if (err.status === 503) return "Нет связи с АТС — попробуйте ещё раз.";
  if (err.code === "pbx_error" || err.status === 502) {
    return "АТС не смогла перехватить звонок — попробуйте ещё раз.";
  }
  return `Не удалось перехватить звонок (код ${err.status}).`;
}
